<script setup lang="ts">
import { api } from '@/api';
import {
  AppPermissionsEnum,
  EvaluationQuantityUsageDto,
  SetupQuantityUsageDto,
  type NumericQuantityDto,
} from '@/api/generator';
import PtwAddButton from '@/components/buttons/PtwAddButton.vue';
import { getDefaultDeleteConfirmationOptions } from '@/components/confirmationModals/defaults';
import type { EntityInfo } from '@/components/confirmationModals/types';
import { useActionWithConfirmation } from '@/components/confirmationModals/useActionWithConfirmation';
import { TAB_PREFIX } from '@/components/confirmationModals/useFormConfirmationModal';
import ContextMenu from '@/components/ContextMenu/ContextMenu.vue';
import type { ContextMenuAction } from '@/components/ContextMenu/types';
import {
  getDefaultContextMenuActions,
  globalContextMenuHeaderItemDefinition,
} from '@/components/ContextMenu/utils';
import EntityChange from '@/components/EntityHistory/EntityChange.vue';
import CommentCount from '@/components/forms/CommentCount/CommentCount.vue';
import { EMPTY_FIELD_SIGN } from '@/components/forms/utils.ts';
import IdentifierLabel from '@/components/IdentifierLabel/IdentifierLabel.vue';
import OverviewTable from '@/components/OverviewTable/OverviewTable.vue';
import OverviewTableHeader from '@/components/OverviewTable/OverviewTableHeader.vue';
import type { SortItem } from '@/components/OverviewTable/types';
import BasePage from '@/components/pages/BasePage/BasePage.vue';
import BasePageTitle from '@/components/pages/BasePage/BasePageTitle.vue';
import { routenames } from '@/router/routenames';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { alphabeticalStringCompare, localizedEnumCompare } from '@/utils/compareAndSort';
import { translateEntityProperty, truthy } from '@/utils/helper';
import { buildOdataQuery } from '@/utils/odata';
import { getTranslationPrefix } from '@/utils/translationPrefix';
import type { I18nKey } from '@/utils/typings';
import { useLoading } from '@/utils/useLoading';
import { useTranslation } from 'i18next-vue';
import { ref, useTemplateRef, watchEffect } from 'vue';
import { useRouter } from 'vue-router';
import QuantityOverviewFilter from './components/QuantityOverviewFilter.vue';
import type { QuantityOverviewFilterObject, ScopeFilterObject } from './types';
import { getEntityChangeEvent } from '@/components/EntityHistory/entityChange';
import { useRemainingViewportHeight } from '@/utils/useRemainingVueport';
import { useTableScrollElement } from '@/utils/vuetifyMenuHelper';

const { hasPermissions } = usePermissionsStore();
const { t } = useTranslation();
const tableRef = useTemplateRef<HTMLDivElement>('table');
const { height } = useRemainingViewportHeight(tableRef);
const tableScrollElement = useTableScrollElement(tableRef);

const headers = [
  {
    title: ``,
    key: 'name',
    sortRaw(quantityA: NumericQuantityDto, quantityB: NumericQuantityDto) {
      const translatedQuantityAName = translateEntityProperty('name', quantityA);
      const translatedQuantityBName = translateEntityProperty('name', quantityB);
      if (!translatedQuantityAName || !translatedQuantityBName) {
        return -1;
      }
      return alphabeticalStringCompare(translatedQuantityAName, translatedQuantityBName);
    },
  },
  {
    title: t('UseInEvaluation_other'),
    key: 'useInEvaluations',
    sortable: false,
  },
  {
    title: t('UseInSetup_other'),
    key: 'useInSetups',
    sortable: false,
  },
  {
    title: t('StateOfUse'),
    key: 'state',
    sort: localizedEnumCompare('StateOfUseEnum'),
  },
  {
    title: t('Added'),
    key: 'creationTime',
  },
  {
    title: t('LastModification'),
    key: 'lastModificationTime',
  },
  globalContextMenuHeaderItemDefinition,
] as const;

const filterObject = ref<QuantityOverviewFilterObject>({});

const quantities = ref<NumericQuantityDto[]>();

const router = useRouter();

const sortBy: SortItem[] = [{ key: 'name', order: 'asc' }];

function getScopeFilter(filter: ScopeFilterObject[]) {
  return {
    or: [
      ...filter.map((entry) => ({
        [entry.usage]: {
          [entry.scope]: {
            any: { [entry.property]: entry.option },
          },
        },
      })),
    ],
  };
}

const {
  action: loadQuantities,
  loading,
  loadingFirstTime,
} = useLoading(async () => {
  const { result } = await api.numericQuantitiesApi.getNumericQuantityList(
    buildOdataQuery({
      search: filterObject.value?.search,
      filter: {
        and: [filterObject.value.evaluationUsage, filterObject.value.setupUsage]
          .filter(truthy)
          .map(getScopeFilter),
      },
    }),
  );
  quantities.value = result?.value || undefined;
}, true);

function navigateTo(id: string, hash?: string) {
  router.push({
    name: routenames.quantities.details,
    hash,
    params: { id },
  });
}

const { action: deleteAction } = useActionWithConfirmation({
  confirmedAction: async (quantity: NumericQuantityDto) => {
    const { success } = await api.numericQuantitiesApi.deleteNumericQuantity({
      id: quantity.id,
    });

    if (success) {
      loadQuantities();
    }
  },
  confirmationOptions: {
    title: t('Confirmation'),
    confirmButton: t('Delete'),
    hideClose: false,
  },
});

function getContextMenuActions(item: NumericQuantityDto): ContextMenuAction[] {
  const defaultContextMenuActions = getDefaultContextMenuActions(item);
  return [
    {
      ...defaultContextMenuActions.view,
      to: {
        name: routenames.quantities.details,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.edit,
      to: {
        name: routenames.quantities.edit,
        params: { id: item.id },
      },
    },
    {
      ...defaultContextMenuActions.delete,
      callback: () => {
        deleteAction({
          param: item,
          confirmationOptions: getDefaultDeleteConfirmationOptions(
            {
              ...(item as EntityInfo),
              typeName: 'Quantity_one',
            },
            t,
          ),
        });
      },
    },
  ];
}

// list usage
function listUsage(usage: SetupQuantityUsageDto | EvaluationQuantityUsageDto | undefined) {
  const medicalSystemScopes = (usage?.medicalSystemScope || []).map((scope: any) => {
    if (scope?.rtSystemSubCategory) {
      return t(
        `${getTranslationPrefix(
          'medicalSystemScope',
          'rtSystemSubCategory',
        )}.${scope.rtSystemSubCategory}` as I18nKey,
      );
    } else {
      return t(
        `${getTranslationPrefix('medicalSystemScope', 'rtSystemCategory')}.${
          scope.rtSystemCategory
        }` as I18nKey,
      );
    }
  });

  const qaEquipmentScopes = (usage?.qaEquipmentScope || []).map((scope: any) =>
    t(`QaEquipmentSubCategoryEnum.${scope.rtSystemSubCategory}` as I18nKey),
  );
  return [...medicalSystemScopes.filter(truthy), ...qaEquipmentScopes.filter(truthy)].join(', ');
}

watchEffect(loadQuantities);
</script>

<template>
  <BasePage :loading="loadingFirstTime">
    <template #title>
      <BasePageTitle :title="$t('Quantity_other')" />
    </template>

    <template #actions>
      <PtwAddButton
        v-if="hasPermissions([AppPermissionsEnum.GlobalQuantitiesAdd])"
        :to="{ name: routenames.quantities.add }"
      ></PtwAddButton>
    </template>

    <QuantityOverviewFilter v-model="filterObject" />
    <OverviewTable
      ref="table"
      class="mt-2"
      :headers="headers"
      :items="quantities"
      itemLabel="Quantity"
      data-testid="DataTable_Quantity"
      :loading="loading"
      :sortBy="sortBy"
      :height="height"
      fixedHeader
      @click:row="
        (_event, data) => {
          navigateTo(data.item.id);
        }
      "
    >
      <template #header.name="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('Name') }}
          <span class="font-weight-medium">| {{ $t('ID') }}</span>
        </OverviewTableHeader>
      </template>

      <template #header.useInEvaluations="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('UseInEvaluation_other') }} <br />
          <span class="font-weight-medium">{{ $t('TaskCategory_other') }}</span>
        </OverviewTableHeader>
      </template>

      <template #header.useInSetups="headerProps">
        <OverviewTableHeader v-bind="headerProps">
          {{ $t('UseInSetup_other') }} <br />
          <span class="font-weight-medium">
            {{ $t('SetupCategory_other') }}
          </span>
        </OverviewTableHeader>
      </template>

      <template #item.name="{ item }">
        <IdentifierLabel :identifier="item.iuid" :label="translateEntityProperty('name', item)" />
        <CommentCount :value="item" @click.stop="navigateTo(item.id, `#${TAB_PREFIX}comments`)" />
      </template>

      <template #item.useInEvaluations="{ item }">
        {{ listUsage(item.evaluationUsage) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.useInSetups="{ item }">
        {{ listUsage(item.setupUsage) || EMPTY_FIELD_SIGN }}
      </template>

      <template #item.state="{ item }">
        {{ $t(`StateOfUseEnum.${item.state}` as I18nKey) }}
        <EntityChange
          :entityChange="getEntityChangeEvent(item, 'editing')"
          :showEmptyFieldSign="false"
        />
      </template>

      <template #item.creationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'created')" />
      </template>

      <template #item.lastModificationTime="{ item }">
        <EntityChange :entityChange="getEntityChangeEvent(item, 'modified')" />
      </template>

      <template #item.contextMenu="{ item }">
        <ContextMenu
          :actions="getContextMenuActions(item)"
          :closeOnScrollElements="tableScrollElement"
        />
      </template>
    </OverviewTable>
  </BasePage>
</template>
